import { Alert, FormHelperText } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers';
import { useQueryClient } from '@tanstack/react-query';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useCreateDeviceDataExportMutation } from '../../../hooks/useCreateDeviceDataExportMutation';
import {
  deviceDataExportsQueryKey,
  isDeviceDataExportPreparing,
  useDeviceDataExports,
} from '../../../hooks/useDeviceDataExports';
import { useGetDevice } from '../../../hooks/useGetDevice';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import Loading from '../Loading.jsx';

const Field = styled.div`
  margin-bottom: 28px;
`;

const Description = styled.p`
  line-height: 1.5;
  margin-bottom: 28px;
`;

const MAXIMUM_EXPORT_RANGE_DAYS = 31;

export const CreateDeviceDataExportRoute: React.FC = () => {
  const { id: deviceId } = useParams<{ id: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { data: device, isLoading: isDeviceLoading } = useGetDevice({ deviceId });
  const { data: dataExports, isLoading: areExportsLoading } = useDeviceDataExports(deviceId);
  const mutation = useCreateDeviceDataExportMutation();
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [error, setError] = useState<string | null>(null);

  useSetModalTitle('Create Data Export');

  const accessLimitDays = device?.currentSubscription?.plan.locationHistoryAccessLimitDays;
  const earliestStart = useMemo(
    () => accessLimitDays ? moment().subtract(accessLimitDays, 'days') : undefined,
    [accessLimitDays],
  );
  const exportInProgress = dataExports?.some(isDeviceDataExportPreparing) ?? false;

  useEffect(() => {
    if (!device || startDate || endDate) {
      return;
    }

    const today = moment();
    setStartDate(today);
    setEndDate(today);
  }, [device, startDate, endDate]);

  const now = moment();
  const calendarDayDifference = startDate && endDate
    ? moment.utc(endDate.format('YYYY-MM-DD'))
      .diff(moment.utc(startDate.format('YYYY-MM-DD')), 'days')
    : null;
  const rangeTooLarge = calendarDayDifference !== null
    && calendarDayDifference >= MAXIMUM_EXPORT_RANGE_DAYS;
  const endBeforeStart = !!startDate && !!endDate && endDate.isBefore(startDate, 'day');
  const dateInFuture = (!!startDate && startDate.isAfter(now, 'day'))
    || (!!endDate && endDate.isAfter(now, 'day'));
  const invalidRange = !startDate || !endDate || endBeforeStart
    || dateInFuture || rangeTooLarge;

  const onCreate = () => {
    if (!startDate || !endDate || invalidRange || exportInProgress) {
      return;
    }

    const currentEarliestStart = accessLimitDays
      ? moment().subtract(accessLimitDays, 'days')
      : undefined;
    const effectiveStartDate = currentEarliestStart
      && startDate.isBefore(currentEarliestStart, 'day')
      ? currentEarliestStart
      : startDate;

    setError(null);
    mutation.mutate(
      {
        deviceId,
        startDate: effectiveStartDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        timeZone: moment.tz.guess(),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: deviceDataExportsQueryKey(deviceId) });
          screenNavigator.exitCurrentScreen(history);
        },
        onError: mutationError => setError(
          mutationError instanceof Error
            ? mutationError.message
            : 'Unable to create the data export. Please check the selected dates.',
        ),
      },
    );
  };

  if (isDeviceLoading || !device) {
    return <Loading />;
  }

  return (
    <>
      <Description>
        Choose the location-history period to export. When it’s ready, you can download the CSV
        from the Data Exports screen.
        {accessLimitDays
          ? ` Your plan includes ${accessLimitDays} days of location history.`
          : ' Your plan has no location-history access limit.'}
      </Description>

      {error && <Field><Alert severity="error">{error}</Alert></Field>}
      {exportInProgress && (
        <Field>
          <Alert severity="info">Another export is being prepared for this device.</Alert>
        </Field>
      )}

      <Field>
        <DatePicker
          renderInput={props => <TextField {...props} fullWidth />}
          label="Start date"
          value={startDate}
          minDate={earliestStart}
          maxDate={now}
          onChange={value => setStartDate(value)}
        />
      </Field>

      <Field>
        <DatePicker
          renderInput={props => <TextField {...props} fullWidth />}
          label="End date"
          value={endDate}
          minDate={earliestStart}
          maxDate={now}
          onChange={value => setEndDate(value)}
        />
        {rangeTooLarge && (
          <FormHelperText error aria-live="polite">
            Choose a range of 31 days or less.
          </FormHelperText>
        )}
      </Field>

      <Button
        color="primary"
        disabled={invalidRange || mutation.isLoading || areExportsLoading || exportInProgress}
        loading={mutation.isLoading}
        onClick={onCreate}
      >
        Create Export
      </Button>
    </>
  );
};
